import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { EmailEditor } from '../common/EmailEditor';
import api, { emailApi, seminarApi, instructorApi } from '../../lib/api';
import { toast } from 'sonner';
import {
    ArrowLeft, Send, Loader2, Mail, Calendar, Users, Plus, X, Clock,
    AlertTriangle, Search, Trash2, Pencil, FileText, CheckCircle2, XCircle,
    ChevronLeft, ChevronRight, Settings2, Eraser,
} from 'lucide-react';

/**
 * Standalone-Seite für Newsletter / Informationsmail-Versand an Akademie-Mitglieder.
 * Ersetzt das frühere Pop-up.
 */
export default function NewsletterPage() {
    const navigate = useNavigate();

    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState('');
    const [subject, setSubject] = useState('');
    const [bodyHtml, setBodyHtml] = useState('');

    // Empfänger
    const [recipientMode, setRecipientMode] = useState('all'); // all | selected
    const [members, setMembers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [memberPickerOpen, setMemberPickerOpen] = useState(false);
    const [memberSearch, setMemberSearch] = useState('');
    const [recipientCount, setRecipientCount] = useState(0);

    // Seminare
    const [seminars, setSeminars] = useState([]);
    const [selectedSeminarIds, setSelectedSeminarIds] = useState([]);
    const [seminarPickerOpen, setSeminarPickerOpen] = useState(false);
    const [seminarCardsPreview, setSeminarCardsPreview] = useState('');

    // Versand
    const [scheduleEnabled, setScheduleEnabled] = useState(false);
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('09:00');
    const [sending, setSending] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [scheduledItems, setScheduledItems] = useState([]);
    const [logs, setLogs] = useState([]);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsSkip, setLogsSkip] = useState(0);
    const [logsStatusFilter, setLogsStatusFilter] = useState('all');
    const [logsSearch, setLogsSearch] = useState('');
    const [logsLoading, setLogsLoading] = useState(false);
    const [retentionOpen, setRetentionOpen] = useState(false);
    const [retentionEnabled, setRetentionEnabled] = useState(true);
    const [retentionDays, setRetentionDays] = useState(540);
    const [cleaningLogs, setCleaningLogs] = useState(false);
    const LOGS_LIMIT = 25;
    const [editingScheduleId, setEditingScheduleId] = useState(null);

    const loadScheduled = async () => {
        try {
            const r = await api.get('/newsletter/scheduled');
            setScheduledItems(r.data || []);
        } catch { setScheduledItems([]); }
    };

    const loadLogs = async (skip = logsSkip) => {
        setLogsLoading(true);
        try {
            const params = { skip, limit: LOGS_LIMIT };
            if (logsStatusFilter && logsStatusFilter !== 'all') params.status = logsStatusFilter;
            const q = logsSearch.trim();
            if (q) params.q = q;
            const r = await api.get('/newsletter/logs', { params });
            const d = r.data || {};
            setLogs(d.items || []);
            setLogsTotal(d.total || 0);
            setLogsSkip(d.skip ?? skip);
        } catch {
            setLogs([]);
            setLogsTotal(0);
        } finally {
            setLogsLoading(false);
        }
    };

    const loadRetention = async () => {
        try {
            const r = await api.get('/newsletter/log-retention');
            setRetentionEnabled(!!r.data?.enabled);
            setRetentionDays(r.data?.retention_days || 540);
        } catch { /* */ }
    };

    const saveRetention = async () => {
        try {
            await api.put('/newsletter/log-retention', {
                enabled: retentionEnabled,
                retention_days: Number(retentionDays) || 540,
            });
            toast.success('Aufbewahrungsdauer gespeichert');
            setRetentionOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        }
    };

    const handleManualCleanup = async () => {
        const days = Number(retentionDays) || 540;
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
        if (!window.confirm(
            `Alle Log-Einträge älter als ${days} Tage (vor ${new Date(cutoff).toLocaleDateString('de-DE')}) endgültig löschen?`
        )) return;
        setCleaningLogs(true);
        try {
            const r = await api.delete('/newsletter/logs/cleanup', { params: { before: cutoff } });
            toast.success(`${r.data?.deleted || 0} Einträge gelöscht`);
            loadLogs(0);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bereinigung fehlgeschlagen');
        } finally {
            setCleaningLogs(false);
        }
    };

    const handleEditScheduled = async (id) => {
        try {
            const r = await api.get(`/newsletter/scheduled/${id}`);
            const job = r.data;
            setSubject(job.subject || '');
            setBodyHtml(job.body_html || '');
            setTemplateId(job.template_id || '');
            setSelectedSeminarIds(job.seminar_ids || []);
            const filt = job.recipient_filter || { mode: 'all' };
            if (filt.mode === 'manual') {
                setRecipientMode('selected');
                setSelectedUserIds(filt.user_ids || []);
            } else {
                setRecipientMode('all');
                setSelectedUserIds([]);
            }
            // Termin in lokale Datums-/Uhrzeit-Felder rückübersetzen
            try {
                const dt = new Date(job.scheduled_at);
                setScheduleDate(dt.toISOString().split('T')[0]);
                setScheduleTime(dt.toISOString().split('T')[1].slice(0, 5));
                setScheduleEnabled(true);
            } catch { /* */ }
            setEditingScheduleId(id);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            toast.info(`Geplanten Versand bearbeiten – Speichern legt einen neuen Job an, der alte wird storniert.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Versand konnte nicht geladen werden');
        }
    };

    const handleCancelScheduled = async (id, subject) => {
        if (!window.confirm(`Geplanten Versand „${subject}" wirklich stornieren?`)) return;
        try {
            await api.delete(`/newsletter/scheduled/${id}`);
            toast.success('Versand storniert');
            loadScheduled();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Stornierung fehlgeschlagen');
        }
    };

    // Initial loads
    useEffect(() => {
        emailApi.listTemplates('newsletter').then(r => setTemplates(r.data || [])).catch(() => setTemplates([]));
        seminarApi.getAll().then(r => {
            const today = new Date().toISOString().split('T')[0];
            setSeminars((r.data || []).filter(s => s.is_published && (s.date || '') >= today));
        }).catch(() => setSeminars([]));
        instructorApi.getAll(false).then(r => setMembers(r.data || [])).catch(() => setMembers([]));
        loadScheduled();
        loadLogs(0);
        loadRetention();
    }, []);

    // Empfänger-Count
    useEffect(() => {
        if (recipientMode === 'selected') {
            setRecipientCount(selectedUserIds.length);
            return;
        }
        api.get('/newsletter/recipient-count', { params: { mode: 'all' } })
            .then(r => setRecipientCount(r.data?.count || 0))
            .catch(() => setRecipientCount(0));
    }, [recipientMode, selectedUserIds.length]);

    // Live-Vorschau der Seminar-Karten
    useEffect(() => {
        if (!selectedSeminarIds.length) { setSeminarCardsPreview(''); return; }
        api.post('/newsletter/preview-cards', { seminar_ids: selectedSeminarIds })
            .then(r => setSeminarCardsPreview(r.data?.html || ''))
            .catch(() => setSeminarCardsPreview(''));
    }, [selectedSeminarIds]);

    const applyTemplate = (id) => {
        setTemplateId(id);
        const t = templates.find(x => x.id === id);
        if (t) { setSubject(t.subject || ''); setBodyHtml(t.body_html || ''); }
    };

    const selectedSeminars = useMemo(
        () => selectedSeminarIds.map(id => seminars.find(s => s.id === id)).filter(Boolean),
        [selectedSeminarIds, seminars],
    );
    const selectedMembers = useMemo(
        () => selectedUserIds.map(id => members.find(m => m.id === id)).filter(Boolean),
        [selectedUserIds, members],
    );
    const filteredMembers = useMemo(() => {
        const q = memberSearch.trim().toLowerCase();
        if (!q) return members;
        return members.filter(m =>
            (m.first_name || '').toLowerCase().includes(q) ||
            (m.last_name || '').toLowerCase().includes(q) ||
            (m.email || '').toLowerCase().includes(q)
        );
    }, [members, memberSearch]);

    const toggleSeminar = (id) => setSelectedSeminarIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    const toggleMember = (id) => setSelectedUserIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

    const buildPayload = () => ({
        subject,
        body_html: bodyHtml,
        template_id: templateId || null,
        recipient_filter: { mode: recipientMode === 'selected' ? 'manual' : 'all',
                            user_ids: recipientMode === 'selected' ? selectedUserIds : null },
        seminar_ids: selectedSeminarIds.length ? selectedSeminarIds : null,
    });

    const handleSendTest = async () => {
        if (!subject.trim() || !bodyHtml.trim()) { toast.error('Betreff und Inhalt sind Pflicht'); return; }
        setSendingTest(true);
        try {
            const r = await api.post('/newsletter/send-test', buildPayload());
            toast.success(r.data?.message || 'Testmail versendet');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Testmail fehlgeschlagen');
        } finally { setSendingTest(false); }
    };

    const handleSend = async () => {
        if (!subject.trim() || !bodyHtml.trim()) { toast.error('Betreff und Inhalt sind Pflicht'); return; }
        if (recipientMode === 'selected' && !selectedUserIds.length) {
            toast.error('Bitte mindestens 1 Mitglied auswählen'); return;
        }
        let scheduleAt = null;
        if (scheduleEnabled) {
            if (!scheduleDate) { toast.error('Bitte Datum für die Planung wählen'); return; }
            scheduleAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
        }
        setSending(true);
        try {
            const r = await api.post('/newsletter/send', { ...buildPayload(), schedule_at: scheduleAt });
            const d = r.data || {};
            // Wenn ein bestehender geplanter Job bearbeitet wurde → alten Job stornieren
            if (editingScheduleId) {
                try { await api.delete(`/newsletter/scheduled/${editingScheduleId}`); } catch { /* */ }
                setEditingScheduleId(null);
            }
            if (d.scheduled) {
                toast.success(d.message || 'Versand geplant');
                setScheduleEnabled(false);
                setScheduleDate('');
                loadScheduled();
            } else {
                toast.success(d.message || `${d.sent || 0} Mails versendet`);
                if ((d.errors || []).length) toast.warning(`${d.errors.length} Versand-Fehler.`);
                loadLogs(0);
                navigate('/admin/instructors');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
        } finally { setSending(false); }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col gap-6 max-w-5xl" data-testid="newsletter-page">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin/instructors')} data-testid="back-to-instructors">
                        <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zu Akademie-Mitglieder
                    </Button>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Mail className="h-5 w-5 text-primary" /> Newsletter / Informationsmail
                        </h1>
                        <p className="text-sm text-muted-foreground">Sammel-Versand an Akademie-Mitglieder mit eingebetteten Seminar-Karten.</p>
                    </div>
                </div>

                {editingScheduleId && (
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50/60 text-sm" data-testid="editing-schedule-banner">
                        <div className="flex items-center gap-2">
                            <Pencil className="h-4 w-4 text-amber-700" />
                            <span>Bearbeitung eines geplanten Versands. Beim Speichern wird der alte Job storniert und ein neuer angelegt.</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingScheduleId(null); setSubject(''); setBodyHtml(''); setSelectedSeminarIds([]); setSelectedUserIds([]); setRecipientMode('all'); setScheduleEnabled(false); setScheduleDate(''); setTemplateId(''); toast.info('Bearbeitung abgebrochen'); }}
                            data-testid="cancel-edit-schedule"
                        >
                            Bearbeitung verwerfen
                        </Button>
                    </div>
                )}
                {/* Empfänger */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Users className="h-4 w-4" /> Empfänger
                            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold" data-testid="newsletter-recipient-count">
                                {recipientCount} {recipientCount === 1 ? 'Empfänger' : 'Empfänger'}
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                        <div className="flex gap-3 flex-wrap">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="radio" name="recmode" checked={recipientMode === 'all'} onChange={() => setRecipientMode('all')} className="accent-primary" data-testid="recmode-all" />
                                An <strong>alle Akademie-Mitglieder</strong>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="radio" name="recmode" checked={recipientMode === 'selected'} onChange={() => setRecipientMode('selected')} className="accent-primary" data-testid="recmode-selected" />
                                An <strong>ausgewählte Mitglieder</strong>
                            </label>
                        </div>
                        {recipientMode === 'selected' && (
                            <div className="flex flex-col gap-2 pt-2">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <Button variant="outline" size="sm" onClick={() => setMemberPickerOpen(true)} data-testid="open-member-picker">
                                        <Plus className="h-3.5 w-3.5 mr-1" /> Mitglieder auswählen ({selectedUserIds.length})
                                    </Button>
                                    {selectedUserIds.length > 0 && (
                                        <Button variant="ghost" size="sm" onClick={() => setSelectedUserIds([])} data-testid="clear-members">Auswahl leeren</Button>
                                    )}
                                </div>
                                {selectedMembers.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {selectedMembers.map(m => (
                                            <span key={m.id} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-xs">
                                                <span className="font-medium">{m.first_name} {m.last_name}</span>
                                                <span className="text-muted-foreground">{m.email}</span>
                                                <button type="button" onClick={() => toggleMember(m.id)} className="text-gray-400 hover:text-red-600">
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Vorlage */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Vorlage laden</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Select value={templateId || 'none'} onValueChange={(v) => v === 'none' ? setTemplateId('') : applyTemplate(v)}>
                            <SelectTrigger data-testid="newsletter-template-select">
                                <SelectValue placeholder="– Vorlage wählen (optional) –" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">– Leere Mail –</SelectItem>
                                {templates.map(t => (
                                    <SelectItem key={t.id} value={t.id} data-testid={`newsletter-template-${t.template_type}`}>
                                        {t.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {templates.length === 0 && (
                            <p className="text-xs text-amber-700 mt-2">
                                Keine Newsletter-Vorlagen gefunden. Unter <strong>Einstellungen → E-Mail-Vorlagen</strong> mit Kategorie „newsletter" anlegen oder Backend neu starten (Seeds).
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* Seminare */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Calendar className="h-4 w-4" /> Seminare einbinden ({selectedSeminars.length})
                            <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={() => setSeminarPickerOpen(true)} data-testid="open-seminar-picker">
                                <Plus className="h-3.5 w-3.5 mr-1" /> Auswählen
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                        {selectedSeminars.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic">Keine Seminare ausgewählt – die Karten-Sektion wird ausgelassen.</p>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {selectedSeminars.map(s => (
                                    <span key={s.id} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-xs">
                                        <span className="font-medium">{s.title}</span>
                                        <span className="text-muted-foreground">{s.course_number}</span>
                                        <button type="button" onClick={() => toggleSeminar(s.id)} className="text-gray-400 hover:text-red-600">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {seminarCardsPreview && (
                            <div className="border rounded-lg p-4 bg-gray-50 max-h-[420px] overflow-y-auto" data-testid="seminar-cards-preview">
                                <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Live-Vorschau der Seminar-Karten</div>
                                <div dangerouslySetInnerHTML={{ __html: seminarCardsPreview }} />
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Inhalt */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Inhalt</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                        <div className="space-y-2">
                            <Label>Betreff *</Label>
                            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z.B. Neues aus der BGPR-Akademie" data-testid="newsletter-subject" />
                        </div>
                        <div className="space-y-2">
                            <Label>Inhalt *</Label>
                            <EmailEditor content={bodyHtml} onChange={setBodyHtml} />
                            <p className="text-xs text-muted-foreground">
                                Tipp: Platzhalter <code className="bg-gray-100 px-1">{'{{seminar_karten_html}}'}</code> markiert die Stelle für die ausgewählten Seminar-Karten. Ohne Platzhalter werden sie am Ende der Mail eingefügt.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Versand */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Versand</CardTitle>
                    </CardHeader>                    <CardContent className="flex flex-col gap-3">
                        <div className={`p-4 rounded-lg border ${scheduleEnabled ? 'bg-amber-50/40 border-amber-200/60' : 'bg-muted/30'}`}>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} data-testid="newsletter-schedule-toggle" />
                                <div className="flex-1">
                                    <p className="font-medium text-sm flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Versand planen statt sofort senden</p>
                                    {scheduleEnabled ? (
                                        <div className="flex flex-wrap gap-2 mt-2 items-end" onClick={(e) => e.preventDefault()}>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Datum</Label>
                                                <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                                                    min={new Date(Date.now() + 5 * 60 * 1000).toISOString().split('T')[0]}
                                                    className="w-44 h-9" data-testid="newsletter-schedule-date" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Uhrzeit</Label>
                                                <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="w-32 h-9" data-testid="newsletter-schedule-time" />
                                            </div>
                                            <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Server-Zeit (UTC).</p>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-muted-foreground mt-1">Sofort-Versand. Scheduler prüft pending-Jobs alle 60 Sekunden.</p>
                                    )}
                                </div>
                            </label>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap pt-2">
                            <Button variant="outline" onClick={handleSendTest} disabled={sendingTest || sending} data-testid="newsletter-test-btn" title="Sendet eine Test-Mail mit aktueller Auswahl an Ihre Admin-E-Mail-Adresse">
                                {sendingTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                Testmail an mich
                            </Button>
                            <div className="flex-1" />
                            <Button variant="outline" onClick={() => navigate('/admin/instructors')} disabled={sending}>Abbrechen</Button>
                            <Button onClick={handleSend} disabled={sending || recipientCount === 0} data-testid="newsletter-send-btn">
                                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                {scheduleEnabled ? `Planen (${recipientCount})` : `Sofort an ${recipientCount} senden`}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Geplante Versendungen */}
                <Card data-testid="scheduled-newsletters-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Clock className="h-4 w-4" /> Geplante Versendungen ({scheduledItems.length})
                            <Button variant="ghost" size="sm" className="ml-auto" onClick={loadScheduled} title="Liste aktualisieren">
                                <Loader2 className="h-3.5 w-3.5" />
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {scheduledItems.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">Keine geplanten Versendungen.</p>
                        ) : (
                            <div className="flex flex-col divide-y">
                                {scheduledItems.map(item => {
                                    const dt = new Date(item.scheduled_at);
                                    const dateLabel = isNaN(dt.getTime()) ? item.scheduled_at : dt.toLocaleString('de-DE', {
                                        day: '2-digit', month: '2-digit', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit',
                                    });
                                    const statusBadge = {
                                        pending: { label: 'Geplant', cls: 'bg-amber-100 text-amber-800' },
                                        running: { label: 'Wird versendet', cls: 'bg-blue-100 text-blue-800' },
                                        failed: { label: 'Fehlgeschlagen', cls: 'bg-red-100 text-red-800' },
                                    }[item.status] || { label: item.status, cls: 'bg-gray-100 text-gray-700' };
                                    return (
                                        <div key={item.id} className="py-3 flex items-start gap-3" data-testid={`scheduled-item-${item.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{item.subject}</p>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                                                    <span className="inline-flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" /> {dateLabel}
                                                    </span>
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${statusBadge.cls}`}>
                                                        {statusBadge.label}
                                                    </span>
                                                    {item.seminar_ids?.length > 0 && (
                                                        <span>{item.seminar_ids.length} Seminar{item.seminar_ids.length === 1 ? '' : 'e'}</span>
                                                    )}
                                                    {item.recipient_filter?.mode && (
                                                        <span>Empfänger: {item.recipient_filter.mode === 'all' ? 'alle' : item.recipient_filter.mode === 'manual' ? `${(item.recipient_filter.user_ids || []).length} ausgewählt` : item.recipient_filter.mode}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {item.status === 'pending' && (
                                                <div className="flex items-center gap-0.5">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEditScheduled(item.id)}
                                                        className="text-gray-600 hover:text-primary hover:bg-primary/10"
                                                        data-testid={`edit-scheduled-${item.id}`}
                                                        title="Geplanten Versand bearbeiten"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleCancelScheduled(item.id, item.subject)}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        data-testid={`cancel-scheduled-${item.id}`}
                                                        title="Geplanten Versand stornieren"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Versand-Logfile */}
                <Card data-testid="newsletter-logs-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                            <FileText className="h-4 w-4" /> Versendete Informationsmails
                            <span className="text-xs font-normal text-muted-foreground">
                                {logsTotal} {logsTotal === 1 ? 'Eintrag' : 'Einträge'}
                            </span>
                            <div className="ml-auto flex items-center gap-1">
                                <Popover open={retentionOpen} onOpenChange={setRetentionOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="ghost" size="sm" title="Aufbewahrungsdauer einstellen" data-testid="logs-retention-btn">
                                            <Settings2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80" align="end">
                                        <div className="flex flex-col gap-3">
                                            <p className="text-sm font-semibold">Aufbewahrung & Auto-Bereinigung</p>
                                            <p className="text-xs text-muted-foreground">
                                                Logs werden automatisch täglich (03:30 UTC) bereinigt, sobald sie älter als die Aufbewahrungsdauer sind. DSGVO-konforme Datenminimierung.
                                            </p>
                                            <div className="flex items-center justify-between gap-2">
                                                <Label className="text-sm">Auto-Bereinigung aktiv</Label>
                                                <Switch checked={retentionEnabled} onCheckedChange={setRetentionEnabled} data-testid="retention-enabled-switch" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Aufbewahrung (Tage)</Label>
                                                <Input
                                                    type="number"
                                                    min={30}
                                                    max={3650}
                                                    value={retentionDays}
                                                    onChange={(e) => setRetentionDays(e.target.value)}
                                                    className="h-9"
                                                    data-testid="retention-days-input"
                                                />
                                                <p className="text-[11px] text-muted-foreground">
                                                    {Math.round((Number(retentionDays) || 0) / 30)} Monate · Standard: 540 Tage (18 Monate)
                                                </p>
                                            </div>
                                            <div className="flex justify-end gap-2 pt-1">
                                                <Button variant="ghost" size="sm" onClick={() => setRetentionOpen(false)}>Abbrechen</Button>
                                                <Button size="sm" onClick={saveRetention} data-testid="save-retention">Speichern</Button>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleManualCleanup}
                                    disabled={cleaningLogs}
                                    title="Alte Logs jetzt manuell löschen"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid="manual-cleanup-btn"
                                >
                                    {cleaningLogs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eraser className="h-3.5 w-3.5" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => loadLogs(logsSkip)} disabled={logsLoading} title="Liste aktualisieren">
                                    <Loader2 className={`h-3.5 w-3.5 ${logsLoading ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                        {/* Filterleiste */}
                        <div className="flex flex-wrap items-end gap-2">
                            <div className="flex-1 min-w-[180px] relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                <Input
                                    value={logsSearch}
                                    onChange={(e) => setLogsSearch(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') loadLogs(0); }}
                                    placeholder="Suche E-Mail / Name / Betreff"
                                    className="pl-8 h-9"
                                    data-testid="logs-search-input"
                                />
                            </div>
                            <Select value={logsStatusFilter} onValueChange={(v) => { setLogsStatusFilter(v); setTimeout(() => loadLogs(0), 0); }}>
                                <SelectTrigger className="w-40 h-9" data-testid="logs-status-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Status</SelectItem>
                                    <SelectItem value="sent">Versendet</SelectItem>
                                    <SelectItem value="error">Fehler</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button variant="outline" size="sm" className="h-9" onClick={() => loadLogs(0)} data-testid="logs-search-btn">
                                Suchen
                            </Button>
                            {(logsSearch || logsStatusFilter !== 'all') && (
                                <Button variant="ghost" size="sm" className="h-9" onClick={() => { setLogsSearch(''); setLogsStatusFilter('all'); setTimeout(() => loadLogs(0), 0); }} data-testid="logs-reset-btn">
                                    Zurücksetzen
                                </Button>
                            )}
                        </div>

                        {logs.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">
                                {logsTotal === 0 ? 'Noch keine versendeten Informationsmails.' : 'Keine Einträge im aktuellen Filter.'}
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-xs text-muted-foreground uppercase tracking-wide">
                                            <th className="text-left px-3 py-2 font-medium">Zeitpunkt</th>
                                            <th className="text-left px-3 py-2 font-medium">Empfänger</th>
                                            <th className="text-left px-3 py-2 font-medium">Betreff</th>
                                            <th className="text-center px-3 py-2 font-medium w-20">Seminare</th>
                                            <th className="text-center px-3 py-2 font-medium w-24">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map(log => {
                                            const dt = new Date(log.sent_at);
                                            const dateLabel = isNaN(dt.getTime()) ? log.sent_at : dt.toLocaleString('de-DE', {
                                                day: '2-digit', month: '2-digit', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            });
                                            const ok = log.status === 'sent';
                                            return (
                                                <tr key={log.id} className="border-b last:border-0 hover:bg-gray-50/50" data-testid={`log-row-${log.id}`}>
                                                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{dateLabel}</td>
                                                    <td className="px-3 py-2">
                                                        <p className="font-medium text-sm">{log.recipient_name || '—'}</p>
                                                        <p className="text-xs text-muted-foreground">{log.recipient_email}</p>
                                                    </td>
                                                    <td className="px-3 py-2 text-sm">{log.subject}</td>
                                                    <td className="px-3 py-2 text-center text-xs text-muted-foreground">{log.seminar_count ?? '—'}</td>
                                                    <td className="px-3 py-2 text-center">
                                                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${ok ? 'text-emerald-700' : 'text-red-600'}`} title={log.error || ''}>
                                                            {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                                                            {ok ? 'Versendet' : 'Fehler'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {logsTotal > LOGS_LIMIT && (
                            <div className="flex items-center justify-between gap-2 pt-1 border-t" data-testid="logs-pagination">
                                <p className="text-xs text-muted-foreground">
                                    {logsSkip + 1}–{Math.min(logsSkip + logs.length, logsTotal)} von {logsTotal}
                                </p>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => loadLogs(Math.max(0, logsSkip - LOGS_LIMIT))}
                                        disabled={logsSkip === 0 || logsLoading}
                                        data-testid="logs-prev-btn"
                                    >
                                        <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Zurück
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => loadLogs(logsSkip + LOGS_LIMIT)}
                                        disabled={logsSkip + LOGS_LIMIT >= logsTotal || logsLoading}
                                        data-testid="logs-next-btn"
                                    >
                                        Weiter <ChevronRight className="h-3.5 w-3.5 ml-1" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Mitglieder-Picker */}
            <Dialog open={memberPickerOpen} onOpenChange={setMemberPickerOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="member-picker">
                    <DialogHeader>
                        <DialogTitle>Mitglieder auswählen</DialogTitle>
                    </DialogHeader>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Suche nach Name oder E-Mail" className="pl-9" data-testid="member-search" />
                    </div>
                    <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1 mt-3">
                        {filteredMembers.length === 0 && (
                            <p className="text-sm text-muted-foreground italic px-2 py-6 text-center">Keine Mitglieder gefunden.</p>
                        )}
                        {filteredMembers.map(m => (
                            <label key={m.id} className="flex items-start gap-3 p-2.5 rounded border hover:bg-gray-50 cursor-pointer" data-testid={`member-pick-${m.id}`}>
                                <input type="checkbox" checked={selectedUserIds.includes(m.id)} onChange={() => toggleMember(m.id)} className="w-4 h-4 mt-0.5 accent-primary" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm">{m.first_name} {m.last_name}</p>
                                    <p className="text-xs text-muted-foreground">{m.email}</p>
                                </div>
                            </label>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setMemberPickerOpen(false)}>Übernehmen ({selectedUserIds.length})</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Seminar-Picker */}
            <Dialog open={seminarPickerOpen} onOpenChange={setSeminarPickerOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Seminare auswählen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        {seminars.length === 0 && (
                            <p className="text-sm text-muted-foreground italic">Keine veröffentlichten zukünftigen Seminare gefunden.</p>
                        )}
                        {seminars.map(s => (
                            <label key={s.id} className="flex items-start gap-3 p-3 rounded border hover:bg-gray-50 cursor-pointer" data-testid={`seminar-pick-${s.id}`}>
                                <input type="checkbox" checked={selectedSeminarIds.includes(s.id)} onChange={() => toggleSeminar(s.id)} className="w-4 h-4 mt-0.5 accent-primary" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm">{s.title}</p>
                                    <p className="text-xs text-muted-foreground">{s.course_number} · {s.date} · {s.location || '—'}</p>
                                </div>
                            </label>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setSeminarPickerOpen(false)}>Übernehmen ({selectedSeminarIds.length})</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
